import { useEffect, useState, useCallback } from 'react';
import { Plus, Key, Trash2, Edit2, AlertTriangle, X, Save } from 'lucide-react';
import { api } from '../../api/client';
import { ConfirmDialog } from '../../components/ConfirmDialog';
import type { AdminSession } from '../../auth/auth';
import { useEscapeToClose } from '../../hooks/useEscapeToClose';

type AdminUser = {
  id: string;
  email: string;
  fullName: string;
  role: 'admin' | 'support' | 'readonly' | 'reseller';
  isActive: boolean;
  lastLoginAt: string | null;
  createdAt: string;
};

const ROLE_LABELS: Record<string, { label: string; color: string }> = {
  admin: { label: 'Admin', color: '#a78bfa' },
  support: { label: 'Support', color: '#60a5fa' },
  readonly: { label: 'Read-only', color: '#94a3b8' },
  reseller: { label: 'Prodavac', color: '#34d399' },
};

export function AdminUsersPage({ session }: { session: AdminSession }) {
  const [admins, setAdmins] = useState<AdminUser[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState<AdminUser | null>(null);
  const [creating, setCreating] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<AdminUser | null>(null);

  const isAdminRole = session.admin.role === 'admin';

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const rows = await api.get<AdminUser[]>('/admin/users');
      setAdmins(rows);
    } catch (e: any) {
      setError(e?.data?.message || e?.message || 'Greška');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    await api.del(`/admin/users/${deleteTarget.id}`);
    refresh();
  };

  const formatDate = (iso: string | null) => {
    if (!iso) return '-';
    return new Date(iso).toLocaleString('sr-RS', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  return (
    <div>
      <header style={{ marginBottom: 24, display: 'flex', alignItems: 'center', gap: 16 }}>
        <div>
          <h1 style={{ margin: 0, fontSize: 24, fontWeight: 700, color: 'var(--color-text-strong)' }}>Admin korisnici</h1>
          <div style={{ marginTop: 4, fontSize: 13, color: 'var(--color-text-muted)' }}>
            Nalozi sa pristupom admin panelu (sysadmin role)
          </div>
        </div>
        <div style={{ flex: 1 }} />
        {isAdminRole && (
          <button onClick={() => setCreating(true)} style={btnPrimary}>
            <Plus size={14} /> Novi admin
          </button>
        )}
      </header>

      {error && (
        <div style={errorBox}>
          <AlertTriangle size={14} /> {error}
        </div>
      )}

      <div style={{ background: 'var(--color-bg-surface)', borderRadius: 10, overflow: 'hidden', border: '1px solid var(--color-border)' }}>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: 'var(--color-bg)' }}>
                <th style={th}>Ime</th>
                <th style={th}>Email</th>
                <th style={th}>Role</th>
                <th style={th}>Status</th>
                <th style={th}>Poslednji login</th>
                <th style={{ ...th, width: 100, textAlign: 'right' }}>Akcije</th>
              </tr>
            </thead>
            <tbody>
              {!loading && admins.length === 0 && (
                <tr><td colSpan={6} style={{ ...td, textAlign: 'center', padding: 40, color: 'var(--color-text-muted)' }}>
                  <Key size={32} style={{ margin: '0 auto 8px', opacity: 0.3 }} />
                  <div>Nema admin korisnika</div>
                </td></tr>
              )}
              {admins.map((a) => {
                const isSelf = a.id === session.admin.id;
                const roleStyle = ROLE_LABELS[a.role] || ROLE_LABELS.readonly;
                return (
                  <tr key={a.id}>
                    <td style={{ ...td, color: 'var(--color-text-strong)', fontWeight: 600 }}>
                      {a.fullName}
                      {isSelf && <span style={{ marginLeft: 8, fontSize: 10, padding: '1px 6px', borderRadius: 6, background: 'rgba(124,58,237,0.2)', color: '#c4b5fd', fontWeight: 700 }}>VI</span>}
                    </td>
                    <td style={{ ...td, color: 'var(--color-text-muted)', fontFamily: 'monospace', fontSize: 12 }}>{a.email}</td>
                    <td style={td}>
                      <span style={{ fontSize: 10, padding: '2px 8px', borderRadius: 8, background: 'rgba(100,116,139,0.2)', color: roleStyle.color, fontWeight: 700 }}>
                        {roleStyle.label}
                      </span>
                    </td>
                    <td style={td}>
                      {a.isActive
                        ? <span style={badgeActive}>Aktivan</span>
                        : <span style={badgeInactive}>Neaktivan</span>}
                    </td>
                    <td style={{ ...td, color: 'var(--color-text-muted)', fontSize: 12 }}>{formatDate(a.lastLoginAt)}</td>
                    <td style={{ ...td, textAlign: 'right' }}>
                      {(isAdminRole || isSelf) && (
                        <button onClick={() => setEditing(a)} style={iconBtn} title="Izmeni"><Edit2 size={13} /></button>
                      )}
                      {isAdminRole && !isSelf && (
                        <button onClick={() => setDeleteTarget(a)} style={{ ...iconBtn, color: '#fca5a5' }} title="Deaktiviraj"><Trash2 size={13} /></button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {creating && (
        <AdminUserFormDialog
          user={null}
          isAdminRole={isAdminRole}
          onClose={() => setCreating(false)}
          onSaved={() => { setCreating(false); refresh(); }}
        />
      )}

      {editing && (
        <AdminUserFormDialog
          user={editing}
          isAdminRole={isAdminRole}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); refresh(); }}
        />
      )}

      {deleteTarget && (
        <ConfirmDialog
          title="Deaktiviraj admin korisnika"
          message={`Da li ste sigurni da želite da deaktivirate "${deleteTarget.fullName}" (${deleteTarget.email})? Više neće moći da se loguje na admin panel.`}
          confirmLabel="Deaktiviraj"
          variant="danger"
          onConfirm={confirmDelete}
          onClose={() => setDeleteTarget(null)}
        />
      )}
    </div>
  );
}

function AdminUserFormDialog({ user, isAdminRole, onClose, onSaved }: {
  user: AdminUser | null;
  isAdminRole: boolean;
  onClose: () => void;
  onSaved: () => void;
}) {
  useEscapeToClose(onClose);
  const isEdit = !!user;
  const [email, setEmail] = useState(user?.email || '');
  const [fullName, setFullName] = useState(user?.fullName || '');
  const [password, setPassword] = useState('');
  const [role, setRole] = useState<'admin' | 'support' | 'readonly' | 'reseller'>(user?.role || 'admin');
  const [isActive, setIsActive] = useState(user?.isActive ?? true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim() || !fullName.trim()) { setError('Email i ime su obavezni'); return; }
    if (!isEdit && !password) { setError('Lozinka je obavezna'); return; }
    if (password && password.length < 6) { setError('Lozinka mora imati najmanje 6 karaktera'); return; }

    setSaving(true);
    setError(null);
    try {
      if (isEdit) {
        const body: any = { email: email.trim(), fullName: fullName.trim() };
        if (password) body.password = password;
        if (isAdminRole) {
          body.role = role;
          body.isActive = isActive;
        }
        await api.put(`/admin/users/${user!.id}`, body);
      } else {
        await api.post('/admin/users', {
          email: email.trim(),
          fullName: fullName.trim(),
          password,
          role,
        });
      }
      onSaved();
    } catch (e: any) {
      setError(e?.data?.message || e?.message || 'Greška pri čuvanju');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={overlay}>
      <div onClick={(e) => e.stopPropagation()} style={dialog}>
        <header style={dialogHeader}>
          <h3 style={{ margin: 0, fontSize: 17, fontWeight: 700, color: 'var(--color-text-strong)' }}>
            {isEdit ? `Izmena: ${user!.fullName}` : 'Novi admin korisnik'}
          </h3>
          <button onClick={onClose} style={closeBtn}><X size={16} /></button>
        </header>

        <form onSubmit={handleSubmit} style={{ padding: 24, display: 'flex', flexDirection: 'column', gap: 14 }}>
          <Field label="Puno ime *">
            <input type="text" value={fullName} onChange={(e) => setFullName(e.target.value)} autoFocus required style={input} />
          </Field>
          <Field label="Email *">
            <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required style={input} />
          </Field>
          <Field label={isEdit ? 'Nova lozinka (ostavi prazno za bez izmene)' : 'Lozinka *'}>
            <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} required={!isEdit} minLength={6} style={input} placeholder="••••••" />
          </Field>
          {isAdminRole && (
            <Field label="Role *">
              <select value={role} onChange={(e) => setRole(e.target.value as any)} style={input}>
                <option value="admin">Admin (puna prava)</option>
                <option value="support">Support (čitanje + izmene bez brisanja)</option>
                <option value="readonly">Read-only (samo čitanje)</option>
                <option value="reseller">Prodavac (samo svoji klijenti)</option>
              </select>
            </Field>
          )}
          {isEdit && isAdminRole && (
            <Field label="">
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                <input type="checkbox" checked={isActive} onChange={(e) => setIsActive(e.target.checked)} style={{ accentColor: 'var(--color-primary)' }} />
                <span style={{ fontSize: 13, color: 'var(--color-text)' }}>Aktivan (može se logovati)</span>
              </label>
            </Field>
          )}

          {error && <div style={errorBox}><AlertTriangle size={14} /> {error}</div>}

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginTop: 16, width: '100%', maxWidth: 360, marginLeft: 'auto', marginRight: 'auto' }}>
            <button type="button" onClick={onClose} style={{ ...btnSecondary, width: '100%', textAlign: 'center' }}>Otkaži</button>
            <button type="submit" disabled={saving} style={{ ...btnPrimary, width: '100%', justifyContent: 'center', opacity: saving ? 0.5 : 1 }}>
              <Save size={14} /> {saving ? 'Čuvanje...' : 'Sačuvaj'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      {label && <label style={{ display: 'block', fontSize: 12, color: 'var(--color-text-muted)', marginBottom: 4, fontWeight: 500 }}>{label}</label>}
      {children}
    </div>
  );
}

const th: React.CSSProperties = { padding: '12px 14px', fontSize: 11, fontWeight: 600, color: 'var(--color-text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', textAlign: 'left', borderBottom: '1px solid var(--color-border)' };
const td: React.CSSProperties = { padding: '12px 14px', fontSize: 13, color: 'var(--color-text)', borderBottom: '1px solid var(--color-border)' };
const btnPrimary: React.CSSProperties = { display: 'flex', alignItems: 'center', gap: 6, padding: '8px 14px', borderRadius: 6, border: 'none', background: 'var(--color-primary)', color: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer' };
const btnSecondary: React.CSSProperties = { padding: '9px 16px', borderRadius: 6, background: 'var(--color-bg)', color: 'var(--color-text)', border: '1px solid var(--color-border)', fontSize: 13, fontWeight: 500, cursor: 'pointer' };
const iconBtn: React.CSSProperties = { background: 'transparent', border: 'none', color: 'var(--color-text-muted)', padding: 6, marginLeft: 4, borderRadius: 4, cursor: 'pointer' };
const errorBox: React.CSSProperties = { display: 'flex', alignItems: 'center', gap: 8, padding: '10px 14px', borderRadius: 6, background: 'rgba(239,68,68,0.15)', color: '#fca5a5', fontSize: 13, marginBottom: 16 };
const overlay: React.CSSProperties = { position: 'fixed', inset: 0, zIndex: 50, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(0,0,0,0.6)' };
const dialog: React.CSSProperties = { width: '100%', maxWidth: 480, background: 'var(--color-bg-surface)', borderRadius: 12, boxShadow: '0 20px 60px rgba(0,0,0,0.5)', border: '1px solid var(--color-border)' };
const dialogHeader: React.CSSProperties = { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '18px 24px', borderBottom: '1px solid var(--color-border)' };
const closeBtn: React.CSSProperties = { background: 'transparent', border: 'none', color: 'var(--color-text-muted)', cursor: 'pointer', padding: 4 };
const input: React.CSSProperties = { width: '100%', padding: '9px 12px', background: 'var(--color-bg)', color: 'var(--color-text-strong)', border: '1px solid var(--color-border)', borderRadius: 6, fontSize: 13, outline: 'none' };
const badgeActive: React.CSSProperties = { fontSize: 10, padding: '2px 8px', borderRadius: 8, background: 'rgba(16,185,129,0.15)', color: '#6ee7b7', fontWeight: 700 };
const badgeInactive: React.CSSProperties = { fontSize: 10, padding: '2px 8px', borderRadius: 8, background: 'rgba(100,116,139,0.2)', color: '#94a3b8', fontWeight: 700 };
